import { onCleanup } from "solid-js";

import { fetchJSON } from "~/api";

import type { LiveSlide, SlideStrokes } from "./types";

const POLL_MS = 1000;

/** The screen and the office tablet that scanned the start code keep to the same slide through the server, since the
 *  tablet is another device: each side sends the key of the slide a person moved it to, and looks every second for a
 *  move newer than the last it saw. A move is numbered by the server, so a side never takes back its own, and while its
 *  own moves are out it takes only the latest one's answer. Stops looking while its tab is hidden, and on cleanup.
 *  Given `drawings` (markup_saver.ts), each look also says which drawing this side holds, and hands on a newer one when
 *  the answer carries it. */
export function createLiveSlide(
  url: string,
  onKey: (key: string) => void,
  drawings?: {
    seen: () => string | null;
    take: (drawn: string | null, drawings: SlideStrokes[]) => void;
  },
): { send: (key: string) => void } {
  let seen = -1;
  let sentKey: string | null = null;
  let stopped = false;
  // Moves sent and not yet answered, and the latest of them: while one is out a look may answer from before it, and
  // only the latest move's answer says where this side is now.
  let pending = 0;
  let latest = 0;
  const take = (live: LiveSlide) => {
    if (stopped || live.seq <= seen) return;
    seen = live.seq;
    sentKey = live.key;
    if (live.key) onKey(live.key);
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const look = () => {
    if (stopped) return;
    const next = () => {
      if (!stopped) timer = setTimeout(look, POLL_MS);
    };
    if (document.visibilityState === "hidden") return next();
    const looking = drawings
      ? `${url}?${new URLSearchParams({ drawn: drawings.seen() ?? "" })}`
      : url;
    fetchJSON<LiveSlide>(looking)
      .then((live) => {
        if (pending === 0) take(live);
        if (live.drawings) drawings?.take(live.drawn ?? null, live.drawings);
      })
      .catch(() => {})
      .finally(next);
  };
  look();
  onCleanup(() => {
    stopped = true;
    clearTimeout(timer);
  });

  return {
    send: (key) => {
      // The slide everyone is on already: a follow of someone else's move is not a move of its own.
      if (key === sentKey) return;
      sentKey = key;
      const move = ++latest;
      pending += 1;
      fetchJSON<LiveSlide>(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key }),
      })
        // Its own move answered, or a later one by the other side: taken, so the next look moves nothing back.
        .then((live) => {
          if (move === latest) take(live);
        })
        .catch(() => {})
        .finally(() => {
          pending -= 1;
        });
    },
  };
}
